import { toE164, tt } from '@agos/shared'

/**
 * `/settings-monitoring` — who is told when the system stops answering.
 *
 * **FB-153.** A card headed *Kritické alerty (SMS)* lists three things that send one:
 *
 * > • **Chyba servera 500** – nespracovaná výnimka v API
 * > • **Health check zlyhal** – databáza alebo server nereagujú
 * > • **Záloha zlyhala** – automatická záloha databázy sa nepodarila
 *
 * **One of the three exists.** `HealthAlertService` has exactly one caller — `ScheduledJobs.healthMonitor()`,
 * a 60-second job that runs `SELECT 1` and alerts on a *change*. `GlobalExceptionHandler` mentions no alert
 * path, and `BackupService` writes a failure to `system_logs` and stops there. So a 500 storm and a failed
 * nightly backup both pass in silence, on a page that promises an SMS for each.
 *
 * **FB-154.** The same page states a cooldown, twice — *Max 1 SMS za 30 minút pre každý typ alertu* and
 * *Cooldown: rovnaký typ alertu sa odošle maximálne 1× za 30 minút*. There is no cooldown.
 * `HealthAlertService` is **edge-triggered**, and its docblock says why:
 *
 * > one message when the system stops answering and one when it comes back, never a message a minute for as
 * > long as it is down. An alarm that repeats is an alarm somebody mutes.
 *
 * The two are different promises. Under a cooldown, somebody who slept through the first SMS gets another in
 * half an hour. Under an edge trigger there is no second message — and the page tells them to expect one.
 *
 * **And the recovery message is not guaranteed.** The *is it down* flag lives in memory, deliberately: a
 * restart while the system is down forgets it, so the *back online* message can be lost. The service records
 * that as the same behaviour legacy had.
 *
 * **The two channels do not draw their credentials from the same place.** SMS reads the tenant's BulkGate id
 * and token out of `sms_settings`. E-mail passes `null` to `EmailGateway`, which falls back to the
 * `agos.email.resend-api-key` property — **empty on this deployment** — and sends from a hard-coded
 * `pizzamajstro@gmail.com` rather than the address `/settings-email` configures. So an e-mail alert is not
 * one setting away from working, it is two.
 */

export interface MonitoringForm {
  healthAlertsEnabled: boolean
  healthAlertPhone: string
  healthAlertEmail: string
}

export const EMPTY_MONITORING: MonitoringForm = {
  healthAlertsEnabled: false,
  healthAlertPhone: '',
  healthAlertEmail: '',
}

/** Only the three keys this page owns. `sms_settings` holds twenty and the others belong elsewhere. */
export const MONITORING_FIELDS = Object.keys(EMPTY_MONITORING)

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function formOf(settings: Record<string, unknown> | undefined): MonitoringForm {
  return {
    healthAlertsEnabled: settings?.healthAlertsEnabled === true,
    healthAlertPhone: text(settings?.healthAlertPhone),
    healthAlertEmail: text(settings?.healthAlertEmail),
  }
}

export function payloadOf(form: MonitoringForm): Record<string, unknown> {
  return {
    healthAlertsEnabled: form.healthAlertsEnabled,
    healthAlertPhone: form.healthAlertPhone.trim(),
    healthAlertEmail: form.healthAlertEmail.trim(),
  }
}

// ─── FB-153: what actually raises an alert ────────────────────────────────────

export interface AlertSource {
  key: string
  /** Whether anything in the backend sends an alert for it. */
  wired: boolean
}

/**
 * The three the page listed, and which of them is real.
 *
 * It used to be one of three. `HealthAlertService` had a single caller; `GlobalExceptionHandler`
 * recorded an alert and sent nothing, and `BackupService` wrote its failure to `system_logs` and
 * stopped — so the page listed three protections and two were decoration.
 *
 * **All three send now.** BACKEND.md §9's error funnel requires it of the 500 handler — *500s →
 * system_logs + admin alert* — and the backup one is the case its own service warns about: a backup
 * that silently stopped working is indistinguishable from one that never ran. Each carries its own
 * alert type, so a run of backup failures cannot consume the thirty-minute cooldown a 500 needs.
 */
export const ALERT_SOURCES: readonly AlertSource[] = [
  { key: 'health-check', wired: true },
  { key: 'server-error', wired: true },
  { key: 'backup-failed', wired: true },
]

export function alertSourceLabel(key: string): string {
  if (key === 'health-check') return tt('Systém neodpovedá')
  if (key === 'server-error') return tt('Chyba servera 500')
  return tt('Záloha zlyhala')
}

export function alertSourceNote(source: AlertSource): string {
  if (source.key === 'health-check') {
    return tt('Úloha každých 60 sekúnd overí databázu. Pri zmene stavu odošle jednu správu.')
  }
  if (source.key === 'server-error') {
    return tt(
      'Nespracovaná výnimka sa zapíše do systémového logu a odošle sa SMS. Rozbité nasadenie ich vyrobí stovky, preto sa rovnaký druh upozornenia pošle najviac raz za 30 minút.',
    )
  }
  return tt(
    'Zlyhanie zálohy sa zapíše do systémového logu a odošle sa SMS - s vlastným 30-minútovým limitom, aby ho neumlčala nesúvisiaca chyba servera.',
  )
}

// `wiredSources` was here and is deleted — §9.35, 2026-08-06. It filtered `ALERT_SOURCES` down to the
// ones actually wired, and the page deliberately does the opposite: it maps every source and marks
// each `data-source-wired` yes or no.
//
// **That is the more informative screen.** A source that exists and is not wired is the thing a
// manager needs to see — a list that quietly omitted it would read as *these are all the alerts there
// are*, which is the misreading the marking exists to prevent. All three are wired today, so the
// filter is also a no-op on this data: it would have removed nothing while teaching the next reader
// that hiding them was intended.

// ─── FB-154: what the mechanism guarantees ───────────────────────────────────

/**
 * How the alerts behave, in the words the behaviour deserves.
 *
 * Not *cooldown*. One message on the way down, one on the way back, and nothing in between however long it
 * lasts — plus the caveat that the second one can be lost.
 */
export function triggerNote(): string {
  return tt(
    'Jedna správa, keď systém prestane odpovedať, a jedna, keď sa vráti. Kým je nedostupný, ďalšie sa neposielajú - nie je to opakované upozornenie každých 30 minút.',
  )
}

export function recoveryCaveat(): string {
  return tt(
    'Správa o obnovení sa môže stratiť: to, že systém bol dole, si pamätá bežiaci proces, takže restart počas výpadku o tom zabudne.',
  )
}

// ─── Which channels are actually set up ──────────────────────────────────────

export type ChannelState = 'live' | 'no-contact' | 'no-credentials'

export interface ChannelReadiness {
  sms: ChannelState
  email: ChannelState
}

/**
 * Whether each channel could carry an alert.
 *
 * Both halves are answered by the server now — `GET /health-monitor/channels`. E-mail used to be
 * unanswerable: the alert path passed `null` as the API key, so it used a server property rather than the
 * credential `/settings-email` configures, and nothing reported whether that property was set. It reads
 * `loyalty_settings` like every other mailer now (FB-155), so the same flag means the same thing.
 *
 * `mailConfigured` is `null` while the request is in flight, and that is reported as *no-credentials*
 * rather than assumed live — a screen that guessed the good case would promise a message that never comes,
 * which is the defect this whole item is about.
 */
export function readinessOf(
  form: MonitoringForm,
  smsConfigured: boolean,
  mailConfigured: boolean,
): ChannelReadiness {
  return {
    sms: form.healthAlertPhone.trim() === '' ? 'no-contact' : smsConfigured ? 'live' : 'no-credentials',
    email:
      form.healthAlertEmail.trim() === '' ? 'no-contact' : mailConfigured ? 'live' : 'no-credentials',
  }
}

/** Both flags, from `GET /health-monitor/channels`. False until it answers — see above. */
export function channelReadinessOf(payload: unknown): {
  smsConfigured: boolean
  mailConfigured: boolean
} {
  const row = (payload ?? {}) as Record<string, unknown>
  return {
    smsConfigured: row.smsConfigured === true,
    mailConfigured: row.mailConfigured === true,
  }
}

export function channelMessage(channel: 'sms' | 'email', state: ChannelState): string {
  if (state === 'live') return tt('Nastavené - alert odíde.')
  if (state === 'no-contact') {
    return channel === 'sms' ? tt('Bez čísla neodíde SMS.') : tt('Bez adresy neodíde e-mail.')
  }
  if (channel === 'sms') {
    return tt('Číslo je zadané, ale BulkGate nie je prepojený v nastaveniach SMS.')
  }
  return tt('Adresa je zadaná, ale v nastaveniach e-mailu chýba kľúč - bez neho sa nič neodošle.')
}

/** Whether anything at all would be delivered. */
export function anyChannelLive(readiness: ChannelReadiness): boolean {
  return readiness.sms === 'live' || readiness.email === 'live'
}

export type AlertingState = 'off' | 'nothing-configured' | 'partial' | 'on'

export function alertingState(form: MonitoringForm, readiness: ChannelReadiness): AlertingState {
  if (!form.healthAlertsEnabled) return 'off'
  if (!anyChannelLive(readiness)) return 'nothing-configured'
  return readiness.sms === 'live' && readiness.email === 'live' ? 'on' : 'partial'
}

export function alertingMessage(state: AlertingState): string {
  if (state === 'off') return tt('Upozornenia sú vypnuté - o výpadku sa nedozvie nikto.')
  if (state === 'nothing-configured') {
    return tt('Upozornenia sú zapnuté, ale nie je kam ich poslať.')
  }
  if (state === 'partial') return tt('Odíde len jeden z dvoch kanálov.')
  return tt('Odíde SMS aj e-mail.')
}

// ─── The contacts ─────────────────────────────────────────────────────────────

export function phoneProblem(value: string): 'shape' | null {
  const raw = value.trim()
  if (raw === '') return null
  // The same normalisation `SmsGateway.internationalise` applies, so a number this refuses is one the
  // gateway would refuse too.
  return toE164(raw) === null ? 'shape' : null
}

export function emailProblem(value: string): 'shape' | null {
  const raw = value.trim()
  if (raw === '') return null
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw) ? null : 'shape'
}

export function mayTest(form: MonitoringForm): boolean {
  if (form.healthAlertPhone.trim() === '' && form.healthAlertEmail.trim() === '') return false
  return phoneProblem(form.healthAlertPhone) === null && emailProblem(form.healthAlertEmail) === null
}

/** The test sends whatever is configured and ignores the master switch, like every other test button here. */
export function testNote(form: MonitoringForm): string {
  const base = tt('Odíde skutočná SMS aj e-mail - test obchádza vypínač upozornení.')
  return form.healthAlertsEnabled ? base : `${base} ${tt('Aj keď sú upozornenia vypnuté.')}`
}

export type TestOutcome = 'ok' | 'not_configured' | 'failed'

export function outcomeOf(value: unknown): TestOutcome {
  if (value === 'ok') return 'ok'
  if (value === 'not_configured') return 'not_configured'
  return 'failed'
}

export function outcomeLabel(outcome: TestOutcome): string {
  if (outcome === 'ok') return tt('Odoslané')
  if (outcome === 'not_configured') return tt('Nenakonfigurované')
  return tt('Neodoslané')
}
